import PageHeader from 'components/PageHeader';
import PermissionButton from 'components/buttons/PermissionButton';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftNotice } from 'components/drafts/DraftNotice';
import { DraftMeta, DraftRecordActions } from 'components/drafts/DraftRecordActions';
import { DraftActionsContext } from 'components/drafts/useDraftActions';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useModal } from 'components/modals/useModal.ts';
import { PageHeaderLink } from 'components/page-header/PageHeaderLink.tsx';
import {
    META_LINK_CLASS,
    MetaItem,
    PRIMARY_ACTION_CLASS,
    RecordHeader,
    RecordMoreMenu,
} from 'components/record-header/RecordHeader';
import { RecordLink } from 'components/record-chip/RecordLink';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateProjectMutation } from 'store/api/projects.ts';
import { ProjectDetailsDto, ProjectListItemDto } from 'types/projects';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { useHasPermissions } from 'utils/permissionUtil';
import { useGoBack, useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ProjectAddMattersModal from './components/ProjectAddMattersModal.tsx';
import { ProjectStatusChip } from './components/ProjectStatusChip.tsx';
import { useProjectDelete } from './useProjectDelete';

interface Props {
    project: ProjectDetailsDto;
}

const ProjectPageHeader = ({ project }: Props) => {
    const navigateWithReturn = useNavigateWithReturnState();
    const goBack = useGoBack('/projects');
    const { openModal, closeModal } = useModal();
    const [updateProject] = useUpdateProjectMutation();
    const isNew = useIsNewDesign();
    const canDelete = useHasPermissions(['DELETE.PROJECTS']);
    const handleDelete = useProjectDelete(String(project.id));
    // A draft's header: the Draft chip, who created it and when it was saved, its own actions and its notice.
    const draftActions = useContext(DraftActionsContext);
    const draft = draftActions?.draft;
    const title = project.projectName || t('new-entity.project');
    const draftNotice = draft ? <DraftNotice actions={draftActions} /> : null;
    const { supervisedByEmployee, responsibleEmployee } = project;

    const openConnectCasesModal = (project: ProjectListItemDto) => {
        openModal(
            <ProjectAddMattersModal
                defaultValue={project.matters ?? []}
                onSubmit={(matters) => {
                    void (async () => {
                        try {
                            const updatedDto = {
                                ...project,
                                supervisedByEmployeeId: project.supervisedByEmployee?.id ?? null,
                                responsibleEmployeeId: project.responsibleEmployee?.id ?? null,
                                matterIds: matters.map((m) => m.id),
                            };

                            await updateProject(updatedDto).unwrap();
                            closeModal();
                        } catch (err) {
                            console.error(err);
                            logError(err);
                        }
                    })();
                }}
                onClose={closeModal}
            />
        );
    };

    if (isNew) {
        // The cards edit in place, so no Edit; Delete sits under ⋯.
        const header = (
            <RecordHeader
                chips={
                    <>
                        {draftActions ? <DraftChip /> : null}
                        <ProjectStatusChip status={project.projectStats.status} />
                    </>
                }
                title={title}
                pin={draftActions ? undefined : { kind: 'projects', id: project.id }}
                meta={
                    <>
                        {supervisedByEmployee ? (
                            <MetaItem label={<FormattedMessage id="projects.header-links.supervised-by" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/employee/${supervisedByEmployee.id}`}
                                    label={supervisedByEmployee.name}
                                />
                            </MetaItem>
                        ) : null}
                        {responsibleEmployee ? (
                            <MetaItem label={<FormattedMessage id="projects.header-links.responsible-employee" />}>
                                <RecordLink
                                    className={META_LINK_CLASS}
                                    to={`/employee/${responsibleEmployee.id}`}
                                    label={responsibleEmployee.name}
                                />
                            </MetaItem>
                        ) : null}
                        {project.openedDate ? (
                            <MetaItem label={<FormattedMessage id="matters.opened" />}>
                                <FormattedDate value={project.openedDate} />
                            </MetaItem>
                        ) : null}
                        {draft ? <DraftMeta draft={draft} /> : null}
                    </>
                }
                actions={
                    draftActions ? (
                        <DraftRecordActions actions={draftActions} />
                    ) : (
                        <>
                            <PermissionButton
                                permissions={['UPDATE.PROJECTS']}
                                color="secondary"
                                variant="contained"
                                size="small"
                                className={PRIMARY_ACTION_CLASS}
                                onClick={() => openConnectCasesModal(project)}
                            >
                                <FormattedMessage id="projects.connect-cases" />
                            </PermissionButton>
                            <RecordMoreMenu
                                menuItems={
                                    canDelete
                                        ? [{ title: t('delete'), variant: 'destructive', handler: handleDelete }]
                                        : []
                                }
                            />
                        </>
                    )
                }
            />
        );
        return draftNotice ? (
            <div className="flex flex-col gap-4">
                {header}
                {draftNotice}
            </div>
        ) : (
            header
        );
    }

    return (
        <>
            <PageHeader
                title={title}
                showBreadcrumb
                onBack={goBack}
                buttons={
                    draftActions ? (
                        <div className="flex items-center justify-end gap-2">
                            <DraftRecordActions actions={draftActions} />
                        </div>
                    ) : (
                        <div className="flex flex-row justify-end gap-2">
                            <PermissionButton
                                permissions={['UPDATE.PROJECTS']}
                                variant="outlined"
                                size="small"
                                onClick={() => navigateWithReturn(`/projects/${project.id}/edit`)}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                            <PermissionButton
                                permissions={['UPDATE.PROJECTS']}
                                color="secondary"
                                variant="contained"
                                size="small"
                                onClick={() => openConnectCasesModal(project)}
                            >
                                <FormattedMessage id="projects.connect-cases" />
                            </PermissionButton>
                        </div>
                    )
                }
            >
                <div className="flex w-full flex-row flex-wrap items-center justify-start gap-2 py-4 sm:gap-4">
                    {draftActions ? <DraftChip /> : null}
                    <ProjectStatusChip status={project.projectStats.status} />
                    {supervisedByEmployee ? (
                        <PageHeaderLink
                            labelId="projects.header-links.supervised-by"
                            link={'employee'}
                            id={supervisedByEmployee.id}
                            name={supervisedByEmployee.name}
                        />
                    ) : null}
                    {responsibleEmployee ? (
                        <PageHeaderLink
                            labelId="projects.header-links.responsible-employee"
                            link={'employee'}
                            id={responsibleEmployee.id}
                            name={responsibleEmployee.name}
                        />
                    ) : null}
                    {draft ? <DraftMeta draft={draft} /> : null}
                </div>
            </PageHeader>
            {draftNotice ? <div className="mb-4">{draftNotice}</div> : null}
        </>
    );
};

export default ProjectPageHeader;
